import { getProfile as getProfileApi } from '@/services'
import { GetProfileResponse, Subscription, User } from '@/types'
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

interface UserStore {
  user: User | null
  entitlements: Map<string, Array<string>> | null
  subscriptions: Array<Subscription>
  isLoading: boolean
  emailVerified: boolean
  isConnectedToStream: boolean
  partnerChatID: string[] | null
  partnerChatName: string
  chatClient: any
  channels: []
  emptyChannelList: boolean
  chatUnreadCount: number
  searchTextClaimAccount: string
  verifyEmailModalState: boolean
}

export const GetProfilePrefix = '@User/GetProfile'
export const GetUserStatsPrefix = '@User/GetUserStats'
export const UpdateProfilePrefix = '@User/UpdateProfile'

export const getProfile = createAsyncThunk(GetProfilePrefix, async () => {
  return await getProfileApi()
})

const initialState: UserStore = {
  user: null,
  entitlements: null,
  subscriptions: [],
  isLoading: true,
  emailVerified: false,
  isConnectedToStream: false,
  partnerChatID: null,
  partnerChatName: '',
  chatClient: null,
  channels: [],
  emptyChannelList: false,
  chatUnreadCount: 0,
  searchTextClaimAccount: '',
  verifyEmailModalState: false,
}

const userSlice = createSlice({
  name: 'USER_SLICE',
  initialState,
  reducers: {
    resetUser: (state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ...restInitialState } = initialState
      Object.assign(state, restInitialState)
    },
    updateUser: (state, action) => {
      state.user = action.payload
    },
    updateEntitlements: (state, action) => {
      state.entitlements = action.payload
    },
    updateSubscriptions: (state, action) => {
      state.subscriptions = action.payload
    },
    setEmptyChannelList: (state, action) => {
      state.emptyChannelList = action.payload
    },
    updateChatUnreadState: (state, action) => {
      state.chatUnreadCount = action.payload
    },
    updateStreamConnectState: (state, action) => {
      state.isConnectedToStream = action.payload
    },
    setChatClient: (state, action) => {
      state.chatClient = action.payload
    },
    setChannels: (state, action) => {
      state.chatClient = action.payload
    },
    setPartnerChatIDState: (state, action) => {
      state.partnerChatID = action.payload
    },
    setPartnerChatIDName: (state, action) => {
      state.partnerChatName = action.payload
    },
    receiveSearchTextClaimAccount: (state, action: PayloadAction<string>) => {
      state.searchTextClaimAccount = action.payload
    },
    setVerifyEmailModalState: (state, action: PayloadAction<boolean>) => {
      state.verifyEmailModalState = action.payload
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getProfile.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(getProfile.fulfilled, (state, action: PayloadAction<GetProfileResponse>) => {
      state.user = action.payload.result
      state.isLoading = false
    })
    builder.addCase(getProfile.rejected, (state) => {
      state.isLoading = false
    })
  },
})

const { reducer, actions } = userSlice

export const {
  resetUser,
  updateUser,
  updateEntitlements,
  updateSubscriptions,
  setEmptyChannelList,
  updateChatUnreadState,
  updateStreamConnectState,
  setChatClient,
  setChannels,
  setPartnerChatIDState,
  setPartnerChatIDName,
  receiveSearchTextClaimAccount,
  setVerifyEmailModalState,
  setIsLoading,
} = actions

export default reducer
